import { PropsWithChildren, useCallback, useMemo } from 'react';
import styled, { StyledComponent } from 'styled-components';
import { rgba } from 'polished';

import { mainGridGapColor, cFFFFFF } from '#/colors';
import {
  DataTabs,
  TabData,
  Tabs,
  Tab,
  TabList,
  TabPanels,
  TabPanel,
  TabListProps,
} from '#/components/common/tabs';
import { verticalSpacerCss } from '#/components/shared/Spacer';
import { gradientUnderline } from '#/components/shared/styles';

interface Props {
  readonly activeTab?: TabData['id'];
  readonly onTabChange?: (id: TabData['id'] | null) => void;
  readonly tabData: readonly TabData[];
  readonly className?: string;
}

export default function TradeOverview(props: Props) {
  return <TradeOverviewContent {...props} />;
}

function TradeOverviewContent(props: Props) {
  const { activeTab, onTabChange, className, tabData } = props;

  const onChange = useCallback(
    (idx: number) => {
      const tabId = tabData[idx]?.id ?? null;
      onTabChange?.(tabId);
    },
    [onTabChange, tabData],
  );

  const index = useMemo(() => {
    if (activeTab == null) return undefined;
    return tabData.findIndex((tab) => tab.id === activeTab);
  }, [activeTab, tabData]);

  return (
    <Container className={className}>
      <DataTabs
        index={index}
        onChange={onChange}
        data={tabData}
        containerComponent={StyledTabsContainer}
        tabComponent={StyledTab}
        tabListComponent={CustomTabList}
        tabPanelComponent={StyledTabPanel}
        tabPanelsComponent={StyledTabPanels}
      />
    </Container>
  );
}

function CustomTabList(props: PropsWithChildren<TabListProps>) {
  return <StyledTabList>{props.children}</StyledTabList>;
}

const Container = styled.div`
  --TABS_LIST_HEIGHT: 50px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  overflow: auto;
`;

const StyledTabsContainer = styled(Tabs)`
  display: flex;
  flex: 1 1 0%;
  flex-direction: column;
`;

const TABS_LIST_GAP = 40;

export const StyledTab: StyledComponent<typeof Tab, object> = styled(Tab)`
  color: ${rgba(cFFFFFF, 0.5)};
  padding: 0 7px;
  font-size: 13px;
  border-bottom: none;
  margin-bottom: -1px;
  position: relative;
  font-weight: 700;
  height: var(--TABS_LIST_HEIGHT);

  z-index: 1;

  &:hover {
    color: ${cFFFFFF};
  }

  &[data-selected] {
    color: ${cFFFFFF};
    ${gradientUnderline}
  }

  &:not(:first-of-type):before {
    display: block;
    position: absolute;
    top: 0px;
    left: -${TABS_LIST_GAP / 2}px;
    content: '';
    height: var(--TABS_LIST_HEIGHT);
    ${verticalSpacerCss}
  }
`;

export const StyledTabList: StyledComponent<typeof TabList, object> = styled(
  TabList,
)`
  height: var(--TABS_LIST_HEIGHT);
  border-bottom: 1px solid ${mainGridGapColor};
  padding: 0 16px;
  gap: ${TABS_LIST_GAP}px;
  align-items: center;
`;

const StyledTabPanels = styled(TabPanels)`
  display: flex;
  flex: 1 1 0%;
`;

export const StyledTabPanel: StyledComponent<typeof TabPanel, object> = styled(
  TabPanel,
)`
  --MIN_HEIGHT: calc(var(--TABLE_ROW_HEIGHT) * var(--MIN_VISIBLE_ROWS));

  flex: 1 1 0%;
  overflow: auto;
  position: relative;
  min-height: var(--MIN_HEIGHT, initial);
`;
